import React, { useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Select, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createExpense, dollarsToCents, EXPENSE_CATEGORIES, ExpenseCategory } from '../../services/moneyApi';

interface ExpenseEntryProps {
  /** Optional: pre-link to a work order */
  workOrderId?: number;
  onAdded?: () => void;
}

/**
 * Quick-add expense form. Owner-only (caller ensures this is only mounted for owners).
 * Amounts are entered in dollars; converted to cents before POST.
 */
const ExpenseEntry: React.FC<ExpenseEntryProps> = ({ workOrderId, onAdded }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: {
    category: ExpenseCategory;
    amountDollars: string | number;
    description?: string;
    expenseDate: dayjs.Dayjs;
  }) => {
    const amountCents = dollarsToCents(values.amountDollars);
    setSubmitting(true);
    try {
      await createExpense({
        category: values.category,
        amountCents,
        description: values.description || null,
        workOrderId: workOrderId ?? null,
        expenseDate: values.expenseDate.format('YYYY-MM-DD'),
      });
      form.resetFields();
      onAdded?.();
    } catch (err) {
      console.error('ExpenseEntry submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      data-testid="expense-add"
      initialValues={{
        category: 'other',
        expenseDate: dayjs(),
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Space wrap style={{ width: '100%' }}>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Select a category' }]}
            style={{ marginBottom: 0, minWidth: 140 }}
          >
            <Select
              data-testid="expense-category"
              options={EXPENSE_CATEGORIES.map((c) => ({
                value: c,
                label: c.charAt(0).toUpperCase() + c.slice(1),
              }))}
              style={{ minWidth: 140 }}
            />
          </Form.Item>

          <Form.Item
            name="amountDollars"
            label="Amount ($)"
            rules={[
              { required: true, message: 'Enter amount' },
              {
                validator: (_, val) => {
                  const n = parseFloat(String(val));
                  if (isNaN(n) || n < 0) return Promise.reject(new Error('Must be a positive number'));
                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              data-testid="expense-amount"
              min={0}
              step={0.01}
              precision={2}
              style={{ width: 120 }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="expenseDate"
            label="Date"
            rules={[{ required: true, message: 'Pick a date' }]}
            style={{ marginBottom: 0 }}
          >
            <DatePicker style={{ width: 140 }} />
          </Form.Item>
        </Space>

        <Form.Item name="description" label="Description (optional)" style={{ marginBottom: 0 }}>
          <Input placeholder="e.g. Fuel for Monday job" maxLength={200} />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          icon={<PlusOutlined />}
          loading={submitting}
          data-testid="expense-submit"
          style={{ marginTop: 4 }}
        >
          Add Expense
        </Button>
      </Space>
    </Form>
  );
};

export default ExpenseEntry;
